import { Card, Grid, Typography, Button, FormControlLabel, Checkbox, TextField, Box, Alert, AlertTitle } from '@mui/material';
import React, { useEffect, useState } from 'react'
import { Field, FormikProvider, useFormik } from 'formik';

import * as Yup from "yup";
import api from '../../../redux/features/auth/axiosAuthHelper';
import dayjs from 'dayjs';
import { TextEditField, TextEditReadOnlyField, OrganisationSelectField, UserTypeSelectField } from '../fields/fields';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import colorConfigs from '../../../configs/colorConfigs';
import { UserSessionStorageKey, UserTableParameterStorageService } from '../../../services/sessionStorage';
import RareButton from '../buttons/RareButton';

interface UserDetailProps {
    user_id?: any | null;
    handleRefreshButton: () => void;
    handleBackButton: () => void;
}

const phoneRegEx = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const UserDetailCard: React.FC<UserDetailProps> = ({ user_id, handleRefreshButton, handleBackButton }) => {
    const authData = useSelector((state: RootState) => state.auth);
    const [errors, setErrors] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<any>({})
    const [updatedValues, setUpdatedValues] = useState<any>({})
    const [hasMadeChanges, setHasMadeChanges] = useState<boolean>(false)
    const [isActiveChecked, setIsActiveChecked] = useState(false);
    const [user, setUser] = useState<any>(null);
    const create = !user_id


    useEffect(() => {
        if (user_id) {
            const fetchUser = async () => {
                try {
                    const url = `users/${user_id}`;

                    const response = await api.get(url, {
                        headers: {
                            'Authorization': `Bearer ${authData.access}`
                        }
                    });

                    const fetchedUser = response.data;
                    setUser(fetchedUser);

                } catch (error) {
                    console.error('Error fetching user', error);
                    setErrors(`No User found with ID ${user_id}`)
                    setTimeout(() => {
                        handleBackButton()
                    }, 4000);
                }
            };
            fetchUser();
        }
    }, [user_id]);

    const validationSchema = () => {
        return Yup.object().shape({
            email: Yup.string().required("This field is required"),
        });
    }


    const formik = useFormik({
        enableReinitialize: !create,
        validateOnChange: false,
        validateOnBlur: false,
        initialValues:
            (!create && user) ? {
                id: user.id || '',
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                user_type: user.user_type || '',
                date_joined: user.date_joined ? dayjs(user.date_joined).format('YYYY-MM-DD HH:mm:ss') : "", // Cant edit - Set to null if no date
                last_login: user.last_login ? dayjs(user.last_login).format('YYYY-MM-DD HH:mm:ss') : "Not yet logged in", // Cant edit - Set to null if no date
                is_active: user.is_active || false,
            }
                : {
                    id: '',
                    first_name: '',
                    last_name: '',
                    email: '',
                    user_type: '',
                    date_joined: dayjs(),
                    last_login: dayjs(),
                    is_active: true,
                },
        validationSchema,
        onSubmit: async (values) => {
            setErrors(null);
            const data = authData.isPortalUser && !create ? {
                first_name: values.first_name,
                last_name: values.last_name,
                email: values.email,
                is_active: create ? true : values.is_active,
            } : {
                first_name: values.first_name,
                last_name: values.last_name,
                email: values.email,
                user_type: values.user_type || 'PORTAL_USER',
                is_active: create ? true : values.is_active,
            }

            const emailChanged = initialValues.email !== updatedValues.email // sends password reset email if email is changed
            const activationChanged = initialValues.is_active !== updatedValues.is_active && updatedValues.is_active // Sends email if user has been reactivated

            const url = user ? `users/${user.id}/` : `users/`
            const method = user ? 'patch' : 'post'

            try {
                console.log('sending data', data);

                // 1) Create/update user
                const resp = await api({
                    method,
                    url,
                    data,
                    headers: { Authorization: `Bearer ${authData.access}` },
                });
                console.log('user save response', resp);

                // 2) Maybe send the reset email
                if (create || emailChanged || activationChanged) {
                    const resetResp = await api.post(
                        'auth/password-reset/',
                        { email: values.email },
                        { headers: { 'Content-Type': 'multipart/form-data' } },
                    );
                    console.log('password-reset response', resetResp);
                }

                // 3) Success path
                console.log('User successfully updated');
                handleRefreshButton();
                const count = UserTableParameterStorageService.get(UserSessionStorageKey.count) || 0;
                UserTableParameterStorageService.set(
                    UserSessionStorageKey.count,
                    count + 1,
                );
            } catch (err: any) {
                console.error('Error saving user:', err);

                const resp = err.response;
                if (resp?.status === 400) {
                    // DRF returns arrays per field
                    const field = Object.keys(resp.data)[0];
                    const msgs = Array.isArray(resp.data[field])
                        ? resp.data[field].join(', ')
                        : resp.data[field];
                    setErrors(`${field}: ${msgs}`);
                }
                else if (resp?.status === 403) {
                    setErrors(resp.data.detail || 'You do not have permission.');
                }
                else {
                    setErrors('Unknown error updating User');
                }

                // scroll to the top to reveal the Alert
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
    });

    //  Set checkbox state
    useEffect(() => {
        setIsActiveChecked(formik.values.is_active)
    }, [user, formik.values.is_active]);



    // Effect to set initial form values from user to check for form changes
    useEffect(() => {
        setTimeout(() => {
            setInitialValues(formik.initialValues);
            setUpdatedValues(formik.initialValues);
        }, 500);
        setHasMadeChanges(false);
    }, [user]);

    // Update the updatedValues state when formik values change
    useEffect(() => {
        setUpdatedValues(formik.values);
    }, [formik.values]);

    // Function to check if form has changed so the save button becomes active on edit
    const hasFormChanged = (flatModel: Record<string, any>, formValues: Record<string, any>): boolean => {
        for (const key in formValues) {
            if (key === "expiry_date") {
                const formDate = dayjs(formValues[key]).format('YYYY-MM-DD');
                const initialDate = create ? dayjs(flatModel[key]).format('YYYY-MM-DD') : dayjs(formValues[key]).format('YYYY-MM-DD');
                if (formDate !== initialDate) {
                    console.log("Changed Date", formDate, initialDate)
                    return true;
                }
            } else {
                if (formValues[key] != flatModel[key]) {
                    console.log("Changed Data", formValues[key], flatModel[key])
                    return true;
                }
            }
        }
        return false;
    };

    // Use Effect to check if any changes ahve been made to the form on Edit
    useEffect(() => {
        if (!create && user) {
            if (Object.keys(initialValues).length > 0 && Object.keys(updatedValues).length > 0) {
                const formChanged = hasFormChanged(initialValues, updatedValues);
                setHasMadeChanges(formChanged);
            }
        }
    }, [updatedValues, initialValues, user]);

    // Use Effect set save button active on load if Create and if changes are made on Edit
    useEffect(() => {
        if (create) {
            setHasMadeChanges(true);
        } else {
            setHasMadeChanges(false);
        }
    }, [create]);

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} style={{}}>
                <Box data-testid="Box-3lo3" sx={{ height: "100%", p: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {errors && (
                        <Alert data-testid="Alert-knqx" severity="error" style={{ width: "59.5%" }}>
                            <AlertTitle data-testid="AlertTitle-8fw4">Error</AlertTitle>
                            {errors}
                        </Alert>
                    )}
                    <div data-testid="div-vurq" style={{ width: "100%", height: "100%", margin: "auto", padding: "1em", display: "flex", alignItems: "center" }}>
                        <Grid container spacing={5} alignItems="top" justifyContent="center" style={{ minWidth: "100%", padding: 5 }}>
                            <Grid item xs={12} md={5} style={{ display: "flex", flexDirection: "column", }}>
                                <Card data-testid="Card-2pio" sx={{ pb: 1, boxShadow: 10, height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box data-testid="Box-z4fo" sx={{ display: 'flex' }}>
                                        <Box data-testid="Box-kav6" sx={{ height: '100%', background: colorConfigs.tables.headBg, display: 'flex', alignItems: 'center', borderBottomRightRadius: 3 }}>
                                            <Typography data-testid="Typography-tu4z" sx={{ px: 2, py: 0.5, color: '#fff', fontWeight: 'bold', fontSize: '20px' }}>User Details</Typography>
                                        </Box>
                                    </Box>
                                    <Box data-testid="Box-utu2" sx={{ p: 2, pt: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {!create && <Field data-testid="Field-oc27" name="id" disableds type="text" className="form-control" label="User ID" component={TextEditReadOnlyField} />}
                                        <Box data-testid="Box-2fx7" sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                                            <Field data-testid="Field-w9xk" name="first_name" type="text" className="form-control" label="First Name" component={TextEditField} />
                                            <Field data-testid="Field-0046" name="last_name" type="text" className="form-control" label="Last Name" component={TextEditField} />
                                        </Box>
                                        <Field data-testid="Field-vxyg" name="email" type="text" className="form-control" label="Email" component={TextEditField} />
                                        {!authData.isPortalUser && (
                                            <Box data-testid="Box-zpsk">
                                                <Field data-testid="Field-499o" name="user_type" type="text" className="form-control" label="User Type" component={UserTypeSelectField} />
                                            </Box>
                                        )}
                                        {!create &&
                                            <FormControlLabel sx={{ mt: 0.5 }} control={<Checkbox checked={isActiveChecked} name="is_active"
                                                sx={{
                                                    '&.Mui-checked': {
                                                        color: colorConfigs.buttons.active,
                                                    },
                                                }}
                                                // disabled={authData.isPortalUser}
                                                onChange={(e) => {
                                                    setIsActiveChecked(!isActiveChecked)
                                                    formik.handleChange(e);
                                                }} className="form-control" />}
                                                label={
                                                    <Box data-testid="Box-iqst" display="flex" alignItems="center">
                                                        User is
                                                        <Typography data-testid="Typography-diaj"
                                                            fontSize="smaller"
                                                            sx={{
                                                                paddingX: 0.7,
                                                                border: isActiveChecked ? `2px solid ${colorConfigs.buttons.active}` : `1px solid ${colorConfigs.buttons.inactive}`,
                                                                borderRadius: 2,
                                                                color: isActiveChecked ? colorConfigs.buttons.active : colorConfigs.buttons.inactive,
                                                                marginLeft: 1
                                                            }}
                                                        >
                                                            {isActiveChecked ? 'Active' : 'Inactive'}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        }
                                    </Box>
                                </Card>
                            </Grid>
                            {!create &&

                                <Grid item xs={12} md={5} style={{ display: "flex", flexDirection: "column", }}>
                                    <Card data-testid="Card-rgbw" sx={{ pb: 1, boxShadow: 10, height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Box data-testid="Box-2ux5" sx={{ display: 'flex' }}>
                                            <Box data-testid="Box-abxa" sx={{ height: '100%', background: colorConfigs.tables.headBg, display: 'flex', alignItems: 'center', borderBottomRightRadius: 3 }}>
                                                <Typography data-testid="Typography-eq9p" sx={{ px: 2, py: 0.5, color: '#fff', fontWeight: 'bold', fontSize: '20px' }}>User Stats</Typography>
                                            </Box>
                                        </Box>
                                        <Box data-testid="Box-003e" sx={{ p: 2, pt: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            <Field data-testid="Field-t8jh" name="date_joined" type="text" className="form-control" label="Date Joined" component={TextEditReadOnlyField} />
                                            <Field data-testid="Field-s76v" name="last_login" type="text" className="form-control" label="Last Login" component={TextEditReadOnlyField} />
                                        </Box>
                                    </Card>
                                </Grid>
                            }
                        </Grid>
                    </div>
                    <div data-testid="div-zpb9" className="form-group" style={{ width: "100%", display: 'flex', alignItems: 'center', justifyContent: "space-between" }}>
                        <RareButton data-testid="RareButton-9jec"
                            onClick={() => handleBackButton()}
                            variant="outlined"
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Back
                        </RareButton>
                        <RareButton data-testid="RareButton-hhee"
                            type="submit"
                            variant="success"
                            disabled={!hasMadeChanges}
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Save Changes
                        </RareButton>
                    </div>
                </Box>
            </form>
        </FormikProvider>
    )
}

export default UserDetailCard;